import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { 
  Package, 
  Truck, 
  Clock, 
  MapPin,
  Filter
} from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { getDonations, acceptDonation, Donation } from '../api/donations'
import { updateVolunteerHomeLocation } from '../api/volunteers'
import { isVolunteerIdApproved, volunteerIdRestrictionMessage } from '../utils/volunteerAccess'
import VolunteerIdStatusBanner from '../components/VolunteerIdStatusBanner'
import { distanceMiles, formatMiles } from '../utils/geo'
import { resolveImageUrl } from '../utils/imageUrl'

type DonationWithDistance = Donation & { distanceMiles?: number | null }

const DeliveriesPage = () => {
  const { user, isAuthenticated, refreshUser } = useUser()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [donations, setDonations] = useState<Donation[]>([])
  const [filteredDonations, setFilteredDonations] = useState<DonationWithDistance[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('NEW')
  const [urgencyFilter, setUrgencyFilter] = useState<string>('')
  const [maxMiles, setMaxMiles] = useState<string>(() =>
    searchParams.get('nearby') === '1' ? '25' : ''
  )
  const [sortByDistance, setSortByDistance] = useState(
    () => searchParams.get('nearby') === '1'
  )
  const [savingLocation, setSavingLocation] = useState(false)
  const [locationMessage, setLocationMessage] = useState<string | null>(null)

  const homeLat = user?.homeLatitude
  const homeLng = user?.homeLongitude
  const hasHome =
    typeof homeLat === 'number' &&
    Number.isFinite(homeLat) &&
    typeof homeLng === 'number' &&
    Number.isFinite(homeLng)

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    
    if (user?.userType !== 'VOLUNTEER') {
      navigate('/')
      return
    }
  }, [isAuthenticated, user, navigate])

  useEffect(() => {
    const fetchDonations = async () => {
      if (!isAuthenticated) return
      
      try {
        const data = await getDonations()
        setDonations(data)
      } catch (error) {
        console.error('Error fetching donations:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDonations()
  }, [isAuthenticated])

  const donationsWithDistance: DonationWithDistance[] = useMemo(() => {
    if (!hasHome || homeLat == null || homeLng == null) {
      return donations.map((d) => ({ ...d, distanceMiles: null }))
    }
    return donations.map((d) => {
      const lat = d.pickupLatitude ?? d.donationLat
      const lng = d.pickupLongitude ?? d.donationLng
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        return { ...d, distanceMiles: null }
      }
      return {
        ...d,
        distanceMiles: distanceMiles(homeLat, homeLng, lat, lng)
      }
    })
  }, [donations, hasHome, homeLat, homeLng])

  useEffect(() => {
    let filtered = [...donationsWithDistance]

    if (statusFilter) {
      filtered = filtered.filter(d => d.status === statusFilter)
    }

    if (urgencyFilter) {
      filtered = filtered.filter(d => d.urgency === urgencyFilter)
    }

    const radius = maxMiles ? Number(maxMiles) : null
    if (radius && Number.isFinite(radius) && hasHome) {
      filtered = filtered.filter(
        (d) => typeof d.distanceMiles === 'number' && d.distanceMiles <= radius
      )
    }

    if (sortByDistance && hasHome) {
      filtered.sort((a, b) => {
        const da = typeof a.distanceMiles === 'number' ? a.distanceMiles : Number.POSITIVE_INFINITY
        const db = typeof b.distanceMiles === 'number' ? b.distanceMiles : Number.POSITIVE_INFINITY
        return da - db
      })
    }

    setFilteredDonations(filtered)
  }, [donationsWithDistance, statusFilter, urgencyFilter, maxMiles, sortByDistance, hasHome])

  const handleAcceptDonation = async (id: string) => {
    if (!isVolunteerIdApproved(user)) {
      alert(volunteerIdRestrictionMessage(user))
      return
    }
    try {
      await acceptDonation(id)
      const data = await getDonations()
      setDonations(data)
    } catch (error: any) {
      console.error('Error accepting donation:', error)
      alert(error?.message || 'Failed to accept donation. Please try again.')
    }
  }

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setLocationMessage('Geolocation is not supported by your browser.')
      return
    }
    setSavingLocation(true)
    setLocationMessage(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await updateVolunteerHomeLocation(pos.coords.latitude, pos.coords.longitude)
          await refreshUser()
          setSortByDistance(true)
          if (!maxMiles) setMaxMiles('25')
          setLocationMessage('Home location saved. You can filter by distance now.')
        } catch (e: any) {
          setLocationMessage(e?.message || 'Failed to save location.')
        } finally {
          setSavingLocation(false)
        }
      },
      () => {
        setSavingLocation(false)
        setLocationMessage('Unable to get your location. Please allow location access and try again.')
      },
      { enableHighAccuracy: false, timeout: 10000 }
    )
  }

  const idApproved = isVolunteerIdApproved(user)
  const idRestriction = volunteerIdRestrictionMessage(user)

  const urgencyColors = {
    LOW: 'bg-green-100 text-green-800',
    MEDIUM: 'bg-yellow-100 text-yellow-800',
    HIGH: 'bg-orange-100 text-orange-800',
    URGENT: 'bg-red-100 text-red-800'
  }

  const statusColors = {
    NEW: 'bg-blue-100 text-blue-800',
    ACCEPTED: 'bg-purple-100 text-purple-800',
    PICKED_UP: 'bg-yellow-100 text-yellow-800',
    DELIVERED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    EXPIRED: 'bg-red-100 text-red-800'
  }

  const formatAddress = (donation: Donation) => {
    if (donation.pickupStreet) {
      return `${donation.pickupStreet}, ${donation.pickupCity}, ${donation.pickupState} ${donation.pickupZipCode}`
    }
    return 'Address not available'
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not scheduled'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading deliveries...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Available Deliveries</h1>
          <p className="text-gray-600 mt-2">Browse and accept delivery opportunities in your area</p>
        </div>

        {!idApproved && <VolunteerIdStatusBanner className="mb-6" />}

        {!hasHome && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-blue-950">
              Save your home location to filter pickups by distance. This is only used for nearby
              filtering and is not shown publicly.
            </p>
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={savingLocation}
              className="btn-outline whitespace-nowrap disabled:opacity-60"
            >
              {savingLocation ? 'Saving…' : 'Use my location'}
            </button>
          </div>
        )}
        {locationMessage && (
          <p className="mb-4 text-sm text-gray-700">{locationMessage}</p>
        )}

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-start space-x-4">
            <Filter className="h-5 w-5 text-gray-400 mt-1" />
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="NEW">Available</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="PICKED_UP">In Transit</option>
                  <option value="DELIVERED">Completed</option>
                  <option value="">All Statuses</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Urgency</label>
                <select
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Urgency Levels</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance from home
                </label>
                <select
                  value={maxMiles}
                  onChange={(e) => {
                    setMaxMiles(e.target.value)
                    if (e.target.value) setSortByDistance(true)
                  }}
                  disabled={!hasHome}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">Any distance</option>
                  <option value="5">Within 5 miles</option>
                  <option value="10">Within 10 miles</option>
                  <option value="25">Within 25 miles</option>
                  <option value="50">Within 50 miles</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                  <input
                    type="checkbox"
                    checked={sortByDistance}
                    onChange={(e) => setSortByDistance(e.target.checked)}
                    disabled={!hasHome}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Sort nearest first
                </label>
              </div>
            </div>
          </div>
          {!hasHome && (
            <p className="mt-3 text-xs text-gray-500">
              Distance filters unlock after you save a home location.
            </p>
          )}
        </div>

        {filteredDonations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No deliveries found</h3>
            <p className="text-gray-600">
              {donations.length === 0 
                ? 'There are no deliveries available at the moment. Check back later!'
                : 'Try adjusting your filters to see more results.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredDonations.map((donation) => (
              <div
                key={donation.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
              >
                {donation.imageUrl && (
                  <div className="h-44 w-full overflow-hidden bg-neutral-100">
                    <img
                      src={resolveImageUrl(donation.imageUrl)}
                      alt={donation.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {donation.title}
                      </h3>
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${urgencyColors[donation.urgency]}`}>
                          {donation.urgency}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColors[donation.status]}`}>
                          {formatStatus(donation.status)}
                        </span>
                        {typeof donation.distanceMiles === 'number' && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-primary-50 text-primary-800">
                            {formatMiles(donation.distanceMiles)} away
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 line-clamp-2">{donation.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Package className="h-4 w-4 mr-2 text-gray-400" />
                      <span>Weight: {donation.estimatedWeight}</span>
                    </div>
                    <div className="flex items-start text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                      <span className="flex-1">{formatAddress(donation)}</span>
                    </div>
                    {donation.scheduledDate && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Scheduled: {formatDate(donation.scheduledDate)}</span>
                      </div>
                    )}
                    {donation.supplier && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Truck className="h-4 w-4 mr-2 text-gray-400" />
                        <span>From: {donation.supplier.firstName} {donation.supplier.lastName}</span>
                      </div>
                    )}
                  </div>

                  {donation.items && donation.items.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Items:</p>
                      <div className="flex flex-wrap gap-2">
                        {donation.items.slice(0, 3).map((item, idx) => (
                          <span
                            key={idx}
                            className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                          >
                            {item.name} ({item.quantity} {item.unit})
                          </span>
                        ))}
                        {donation.items.length > 3 && (
                          <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                            +{donation.items.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-2">
                    <Link
                      to={`/delivery/${donation.id}`}
                      className="btn-outline flex-1 text-center"
                    >
                      View Details
                    </Link>
                    {donation.status === 'NEW' && (
                      <button
                        type="button"
                        onClick={() => handleAcceptDonation(donation.id)}
                        disabled={!idApproved}
                        title={!idApproved ? idRestriction : undefined}
                        className="btn-primary flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Accept
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DeliveriesPage
